/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { UserEventHandlerMap } from '../../@types/emitMapper';
import type { XHubChatClient } from '../../client';
import type { XHubChatEvent } from '../event/event';
import { UserEvent } from '../../@types/user';
import { TypedEventEmitter } from '../../emit/typed-event-emitter';

export class User extends TypedEventEmitter<UserEvent, UserEventHandlerMap> {
  private modified = -1;

  /**
   * The 'displayname' of the user if known.
   * @privateRemarks
   * Should be read-only
   */
  public displayName?: string;
  public rawDisplayName?: string;

  /**
   * The 'description' of the user if known.
   */
  public description?: string;
  /**
   * The 'avatar_url' of the user if known.
   * @privateRemarks
   * Should be read-only
   */
  public avatarUrl?: string;
  /**
   * The presence status message if known.
   * @privateRemarks
   * Should be read-only
   */
  public presenceStatusMsg?: string;
  /**
   * The presence enum if known.
   * @privateRemarks
   * Should be read-only
   */
  public presence = 'offline';
  /**
   * Timestamp (ms since the epoch) for when we last received presence data for this user.
   * We can subtract lastActiveAgo from this to approximate an absolute value for when a user was last active.
   * @privateRemarks
   * Should be read-only
   */
  public lastActiveAgo = 0;
  /**
   * The time elapsed in ms since the user interacted proactively with the server,
   * or we saw a message from the user
   * @privateRemarks
   * Should be read-only
   */
  public lastPresenceTs = 0;
  /**
   * Whether we should consider lastActiveAgo to be an approximation
   * and that the user should be seen as active 'now'
   * @privateRemarks
   * Should be read-only
   */
  public currentlyActive = false;
  /**
   * The events describing this user.
   * @privateRemarks
   * Should be read-only
   */
  public events: {
    /** The m.presence event for this user. */
    presence?: XHubChatEvent;
    profile?: XHubChatEvent;
  } = {};

  /**
   * Construct a new User. A User must have an ID and can optionally have extra information associated with it.
   * @param userId - Required. The ID of this user.
   * @param channel_slug - Required. The channel slug of this user.
   * @deprecated use `User.createUser`
   */
  public constructor(public readonly userId: string, public channel_slug: string) {
    super();
    this.displayName = userId;
    this.rawDisplayName = userId;
    this.updateModifiedTime();
  }

  /**
   * Construct a new User whose events will also emit on XHubChatClient.
   * A User must have an ID and can optionally have extra information associated with it.
   * @param userId - Required. The ID of this user.
   * @param channel_slug - Required. The channel slug of this user.
   * @param client - An instance of XHubChatClient object
   * @returns User object with reEmitter setup on client
   */
  public static createUser(userId: string, channel_slug: string, client: XHubChatClient): User {
    const user = new User(userId, channel_slug);

    client.reEmitter.reEmit(user, [
      UserEvent.AvatarUrl,
      UserEvent.DisplayName,
      UserEvent.Presence,
      UserEvent.CurrentlyActive,
      UserEvent.LastPresenceTs,
    ]);
    return user;
  }

  /**
   * The ID of this user.
   */
  get id(): string {
    return this.userId;
  }

  /**
   * Set this user's description.
   * @param description - The new description.
   */
  public setDescription(description: string): void {
    this.description = description;
  }

  /**
   * Get user data to be stored in the database.
   * @returns The user data as a record.
   */
  public getUserData(): Record<string, any> {
    return {
      displayName: this.displayName,
      rawDisplayName: this.rawDisplayName,
      avatarUrl: this.avatarUrl,
      description: this.description,
    };
  }

  /**
   * Update this User with the given presence event. May fire "User.presence",
   * "User.avatarUrl" and/or "User.displayName" if this event updates this user's
   * properties.
   * @param event - The `m.presence` event.
   *
   * @remarks
   * Fires {@link UserEvent.Presence}
   * Fires {@link UserEvent.DisplayName}
   * Fires {@link UserEvent.AvatarUrl}
   */
  public setPresenceEvent(event: XHubChatEvent): void {
    if (event.getType() !== 'm.presence') {
      return;
    }
    const firstFire = this.events.presence === null;
    this.events.presence = event;

    const eventsToFire: UserEvent[] = [];
    if (event.getContent().presence !== this.presence || firstFire) {
      eventsToFire.push(UserEvent.Presence);
    }
    if (event.getContent().avatar_url && event.getContent().avatar_url !== this.avatarUrl) {
      eventsToFire.push(UserEvent.AvatarUrl);
    }
    if (event.getContent().displayname && event.getContent().displayname !== this.displayName) {
      eventsToFire.push(UserEvent.DisplayName);
    }
    if (
      event.getContent().currently_active !== undefined
      && event.getContent().currently_active !== this.currentlyActive
    ) {
      eventsToFire.push(UserEvent.CurrentlyActive);
    }

    this.presence = event.getContent().presence;
    eventsToFire.push(UserEvent.LastPresenceTs);

    if (event.getContent().status_msg) {
      this.presenceStatusMsg = event.getContent().status_msg;
    }
    if (event.getContent().displayname) {
      this.displayName = event.getContent().displayname;
    }
    if (event.getContent().avatar_url) {
      this.avatarUrl = event.getContent().avatar_url;
    }
    this.lastActiveAgo = event.getContent().last_active_ago;
    this.lastPresenceTs = Date.now();
    this.currentlyActive = event.getContent().currently_active;

    this.updateModifiedTime();

    for (const eventToFire of eventsToFire) {
      this.emit(eventToFire, event, this);
    }
  }

  /**
   * Manually set this user's display name. No event is emitted in response to this
   * as there is no underlying MatrixEvent to emit with.
   * @param name - The new display name.
   */
  public setDisplayName(name: string): void {
    const oldName = this.displayName;
    this.displayName = name;
    if (name !== oldName) {
      this.updateModifiedTime();
    }
  }

  /**
   * Manually set this user's non-disambiguated display name. No event is emitted
   * in response to this as there is no underlying MatrixEvent to emit with.
   * @param name - The new display name.
   */
  public setRawDisplayName(name?: string): void {
    this.rawDisplayName = name;
  }

  /**
   * Manually set this user's avatar URL. No event is emitted in response to this
   * as there is no underlying MatrixEvent to emit with.
   * @param url - The new avatar URL.
   */
  public setAvatarUrl(url?: string): void {
    const oldUrl = this.avatarUrl;
    this.avatarUrl = url;
    if (url !== oldUrl) {
      this.updateModifiedTime();
    }
  }

  /**
   * Update the last modified time to the current time.
   */
  private updateModifiedTime(): void {
    this.modified = Date.now();
  }

  /**
   * Get the timestamp when this User was last updated. This timestamp is
   * updated when this User receives a new Presence event which has updated a
   * property on this object. It is updated <i>before</i> firing events.
   * @returns The timestamp
   */
  public getLastModifiedTime(): number {
    return this.modified;
  }

  /**
   * Get the absolute timestamp when this User was last known active on the server.
   * It is *NOT* accurate if this.currentlyActive is true.
   * @returns The timestamp
   */
  public getLastActiveTs(): number {
    return this.lastPresenceTs - this.lastActiveAgo;
  }
}
